/**
 * Страница по ссылке из админки: /join?t=… — регистрация по приглашению, /reset?t=… — новый пароль.
 */
import type { Me } from '@jt/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, get, post, setCsrf } from './api/client';
import { keys } from './api/hooks';
import { Icon } from './components/ui';

export function LinkPage() {
  const loc = useLocation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const kind = loc.pathname.startsWith('/reset') ? 'reset' : 'invite';
  const token = new URLSearchParams(loc.search).get('t') ?? '';
  const info = useQuery({
    queryKey: ['link', token],
    queryFn: () =>
      get<{ kind: 'invite' | 'reset'; email: string }>(`/auth/link/${encodeURIComponent(token)}`),
    enabled: !!token,
    retry: false,
  });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const password = String(f.get('password') ?? '');
    if (password !== String(f.get('password2') ?? '')) return setErr({ password2: 'Пароли не совпадают' });
    setBusy(true);
    setErr({});
    try {
      const me =
        kind === 'invite'
          ? await post<Me>('/auth/join', {
              token,
              email: String(f.get('email') ?? ''),
              name: String(f.get('name') ?? ''),
              password,
            })
          : await post<Me>('/auth/reset', { token, password });
      setCsrf(me.csrfToken);
      qc.setQueryData(keys.me, me);
      nav(kind === 'invite' ? '/settings' : '/applications', { replace: true });
    } catch (x) {
      if (x instanceof ApiError) setErr(Object.keys(x.fields).length ? x.fields : { _: x.message });
      else setErr({ _: 'Нет связи с сервером' });
    } finally {
      setBusy(false);
    }
  };

  const bad = !token || info.isError || (info.data && info.data.kind !== kind);
  return (
    <main className="login">
      <form onSubmit={submit} aria-labelledby="link-title">
        <div className="brand">
          <span className="bic">
            <Icon name="brief" size={18} />
          </span>
          Поиск работы
        </div>
        <h1 id="link-title">{kind === 'invite' ? 'Регистрация по приглашению' : 'Новый пароль'}</h1>
        {bad ? (
          <p role="alert">
            Ссылка недействительна или устарела. Попросите у администратора новую. <a href="/">Ко входу</a>
          </p>
        ) : !info.data ? (
          <p className="hint">Проверяю ссылку…</p>
        ) : (
          <>
            {kind === 'invite' ? (
              <>
                <p className="hint">Задайте пароль — дальше подключите почту и Telegram в «Настройках».</p>
                <label className="f">
                  Почта
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    defaultValue={info.data.email}
                    readOnly={!!info.data.email}
                    aria-invalid={err.email ? true : undefined}
                  />
                  {err.email && <span className="fnote err">{err.email}</span>}
                </label>
                <label className="f">
                  Как к вам обращаться (подпись в письмах)
                  <input name="name" autoComplete="name" />
                </label>
              </>
            ) : (
              <p className="hint">
                Для {info.data.email}. После смены пароля другие устройства выйдут из аккаунта, вход по коду
                (TOTP) выключится.
              </p>
            )}
            <label className="f">
              Пароль (не короче 10 символов)
              <input
                type="password"
                name="password"
                autoComplete="new-password"
                minLength={10}
                required
                aria-invalid={err.password ? true : undefined}
              />
              {err.password && <span className="fnote err">{err.password}</span>}
            </label>
            <label className="f">
              Ещё раз
              <input type="password" name="password2" autoComplete="new-password" minLength={10} required />
              {err.password2 && <span className="fnote err">{err.password2}</span>}
            </label>
            {err._ && (
              <p role="alert" className="err">
                {err._}
              </p>
            )}
            <button className="btn primary" type="submit" disabled={busy}>
              {busy ? 'Сохраняю…' : kind === 'invite' ? 'Зарегистрироваться' : 'Сохранить пароль'}
            </button>
          </>
        )}
      </form>
    </main>
  );
}
